/** @prettier */

import {useState, useMemo, useEffect, Fragment} from 'react';
import {
  Autocomplete,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {debounce} from '@mui/material/utils';
import {useTranslation} from 'react-i18next';

//API import
import {getWeatherSearch} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';

/**
 * 天気予報用地域選択用オートコンプリート
 * @param props props
 * @returns JSX
 */
const FindLocation = (props: {
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  locationData?: apiIF.resParameters.locationInformations | null;
  id?: string;
  class?: string;
}) => {
  const trans = useTranslation().t; //翻訳関数

  const [inputValue, setInputValue] = useState(''); //入力値
  const [value, setValue] =
    useState<apiIF.resParameters.locationInformations | null>(null); //選択した項目
  const [options, setOptions] = useState<
    readonly apiIF.resParameters.locationInformations[]
  >([]); //選択項目
  const [loading, setLoading] = useState(false); //検索中かどうか

  const weatherAPIFetch = useMemo(() => {
    return debounce(
      (
        request: string,
        callback: (response: apiIF.res.getWeatherSearch) => void
      ) => {
        getWeatherSearch({query: request}).then(result => {
          callback(result);
        });
      },
      400
    );
  }, []);

  //APIからの応答が取得でき次第表示を変更
  useEffect(() => {
    setValue(props.locationData !== undefined ? props.locationData : null);
  }, [props.locationData]);

  useEffect(() => {
    let active = true;

    //未入力の場合
    if (inputValue === '') {
      setOptions(value !== null ? [value] : []);
      return undefined;
    }

    //検索
    setLoading(false);

    weatherAPIFetch(inputValue, (response: apiIF.res.getWeatherSearch) => {
      if (active) {
        let newOptions: readonly apiIF.resParameters.locationInformations[] =
          [];

        if (value !== null) {
          newOptions = [value];
        }

        newOptions = [...newOptions, ...response['search-result']];

        setLoading(false);
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, weatherAPIFetch]);

  return (
    <Autocomplete
      data-testid="custom-element"
      id={props.id}
      className={props.class}
      //選択項目
      options={options}
      //入力欄のレンダリング
      renderInput={params => (
        <TextField
          {...params}
          label={`${trans('FindLocation.txtField.label.location')}*`}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
      //文字を補完するかどうか
      autoComplete={true}
      //組み込み検索処理停止
      filterOptions={x => x}
      //選択した項目を候補に表示するかどうか
      filterSelectedOptions={true}
      //表示形式
      getOptionLabel={option => {
        return (
          `${option.name !== '' ? `${option.name}, ` : ''}` +
          `${option.region !== '' ? `${option.region}, ` : ''}` +
          `${option.country !== '' ? `${option.country}` : ''}`
        );
      }}
      //検索中かどうか
      loading={loading}
      //候補がない場合の表示する値
      noOptionsText={trans('FindLocation.noOptionsTxt')}
      //選択時の処理
      onChange={(
        event: any,
        newValue: apiIF.resParameters.locationInformations | null
      ) => {
        setOptions(newValue !== null ? [newValue, ...options] : options);
        setValue(newValue);
        props.setLocation(newValue !== null ? newValue.url : '');
      }}
      //入力値変更時の処理
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      //選択欄のレンダリング
      renderOption={(props, option) => {
        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{display: 'flex', width: 44}}>
                <img
                  src={iconBlack.LEGEND.src}
                  alt={`${trans(
                    `FindLocation.image.alternativeText.${iconBlack.LEGEND.alt}`
                  )}`}
                />
              </Grid>
              <Grid
                item
                sx={{width: 'calc(100% - 44px)', wordWrap: 'break-word'}}>
                <Box component="span">{option.name}</Box>
                <Typography variant="body2" color="text.secondary">
                  {`${option.region !== '' ? `${option.region}, ` : ''}` +
                    `${option.country !== '' ? `${option.country}` : ''}`}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
      //入力欄に表示する値
      value={value}
    />
  );
};
export default FindLocation;
